import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Search, Briefcase, Edit, Trash2, Loader2, 
  FolderOpen, Tag, IndianRupee, Hash
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ICONS = ['Briefcase', 'Camera', 'Video', 'FileText', 'Image', 'Globe', 'Code', 'Megaphone', 'Mail', 'PenTool'];
const COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#14b8a6'];
const UNITS = ['piece', 'hour', 'word', 'post', 'video', 'design', 'page', 'article', 'campaign'];

export default function CRMWorkLibrary() {
  const { token, isAdmin } = useCRMAuth();
  const [categories, setCategories] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showWorkTypeModal, setShowWorkTypeModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingWorkType, setEditingWorkType] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', color: '#6366f1', icon: 'Briefcase' });
  const [workTypeForm, setWorkTypeForm] = useState({ category_id: '', name: '', unit: 'piece', price_per_unit: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [catRes, wtRes] = await Promise.all([
        fetch(`${API}/api/crm/work-categories`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/crm/work-types`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (catRes.ok) setCategories((await catRes.json()).categories);
      if (wtRes.ok) setWorkTypes((await wtRes.json()).work_types);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory 
        ? `${API}/api/crm/work-categories/${editingCategory.id}`
        : `${API}/api/crm/work-categories`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });
      
      if (res.ok) {
        toast.success(editingCategory ? 'Category updated!' : 'Category created!');
        setShowCategoryModal(false);
        setCategoryForm({ name: '', description: '', color: '#6366f1', icon: 'Briefcase' });
        setEditingCategory(null);
        fetchData();
      } else {
        toast.error('Failed to save category');
      }
    } catch (err) {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWorkType = async () => {
    if (!workTypeForm.category_id || !workTypeForm.name) { toast.error('Category and name required'); return; }
    setSaving(true);
    try {
      const method = editingWorkType ? 'PUT' : 'POST';
      const url = editingWorkType 
        ? `${API}/api/crm/work-types/${editingWorkType.id}`
        : `${API}/api/crm/work-types`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...workTypeForm, price_per_unit: parseFloat(workTypeForm.price_per_unit) || 0 })
      });
      
      if (res.ok) {
        toast.success(editingWorkType ? 'Work type updated!' : 'Work type created!');
        setShowWorkTypeModal(false);
        setWorkTypeForm({ category_id: '', name: '', unit: 'piece', price_per_unit: '', description: '' });
        setEditingWorkType(null);
        fetchData();
      } else {
        toast.error('Failed to save work type');
      }
    } catch (err) {
      toast.error('Failed to save work type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Delete this category? Work types under it will remain.')) return;
    try {
      const res = await fetch(`${API}/api/crm/work-categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Category deleted'); fetchData(); }
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleDeleteWorkType = async (id) => {
    if (!confirm('Delete this work type?')) return;
    try {
      const res = await fetch(`${API}/api/crm/work-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Work type deleted'); fetchData(); }
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, description: cat.description || '', color: cat.color, icon: cat.icon });
    setShowCategoryModal(true);
  };

  const openEditWorkType = (wt) => {
    setEditingWorkType(wt);
    setWorkTypeForm({ category_id: wt.category_id, name: wt.name, unit: wt.unit, price_per_unit: wt.price_per_unit?.toString() || '', description: wt.description || '' });
    setShowWorkTypeModal(true);
  };

  const filteredWorkTypes = workTypes.filter(wt => 
    wt.name?.toLowerCase().includes(search.toLowerCase()) ||
    wt.category_name?.toLowerCase().includes(search.toLowerCase())
  );

  const groupedByCategory = filteredWorkTypes.reduce((acc, wt) => {
    const cat = wt.category_name || 'Uncategorized';
    if (!acc[cat]) acc[cat] = { color: '#6366f1', items: [] };
    const catData = categories.find(c => c.name === cat);
    if (catData) acc[cat].color = catData.color;
    acc[cat].items.push(wt);
    return acc;
  }, {});

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Work Library</h1>
          <p className="text-slate-500">Define work categories and types with pricing</p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', description: '', color: '#6366f1', icon: 'Briefcase' }); setShowCategoryModal(true); }}>
              <FolderOpen className="w-4 h-4 mr-2" /> Add Category
            </Button>
            <Button onClick={() => { setEditingWorkType(null); setWorkTypeForm({ category_id: '', name: '', unit: 'piece', price_per_unit: '', description: '' }); setShowWorkTypeModal(true); }} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" /> Add Work Type
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search work types..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
      </Card>

      {/* Categories Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        {categories.map(cat => (
          <Card key={cat.id} className="p-3 hover:shadow-md transition-shadow cursor-pointer group" style={{ borderLeft: `4px solid ${cat.color}` }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cat.color}20` }}>
                  <Briefcase className="w-4 h-4" style={{ color: cat.color }} />
                </div>
                <span className="font-medium text-slate-900 text-sm">{cat.name}</span>
              </div>
              {isAdmin() && (
                <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditCategory(cat)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteCategory(cat.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">{workTypes.filter(w => w.category_id === cat.id).length} types</p>
          </Card>
        ))}
      </div>

      {/* Work Types by Category */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : Object.keys(groupedByCategory).length === 0 ? (
        <Card className="p-12 text-center">
          <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No work types yet</h3>
          <p className="text-slate-500 mb-4">Start by adding categories and work types</p>
          {isAdmin() && (
            <Button onClick={() => setShowCategoryModal(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add Category
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByCategory).map(([category, data]) => (
            <Card key={category} className="overflow-hidden">
              <div className="px-4 py-3 border-b" style={{ backgroundColor: `${data.color}10` }}>
                <h3 className="font-semibold" style={{ color: data.color }}>{category}</h3>
              </div>
              <div className="divide-y">
                {data.items.map(wt => (
                  <div key={wt.id} className="p-4 hover:bg-slate-50 flex items-center justify-between group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${data.color}15` }}>
                        <Tag className="w-5 h-5" style={{ color: data.color }} />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{wt.name}</h4>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Hash className="w-3 h-3" />{wt.unit}</span>
                          <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{wt.price_per_unit || 0}/{wt.unit}</span>
                        </div>
                      </div>
                    </div>
                    {isAdmin() && (
                      <div className="opacity-0 group-hover:opacity-100 flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEditWorkType(wt)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDeleteWorkType(wt.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
              <Input placeholder="e.g., Social Media" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
              <Textarea placeholder="Brief description..." value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})} rows={2} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setCategoryForm({...categoryForm, color: c})} className={`w-8 h-8 rounded-full ${categoryForm.color === c ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingCategory ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Work Type Modal */}
      <Dialog open={showWorkTypeModal} onOpenChange={setShowWorkTypeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingWorkType ? 'Edit Work Type' : 'Add Work Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Category *</label>
              <Select value={workTypeForm.category_id} onValueChange={(v) => setWorkTypeForm({...workTypeForm, category_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
              <Input placeholder="e.g., Instagram Post" value={workTypeForm.name} onChange={(e) => setWorkTypeForm({...workTypeForm, name: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Unit</label>
                <Select value={workTypeForm.unit} onValueChange={(v) => setWorkTypeForm({...workTypeForm, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Price/Unit (₹)</label>
                <Input type="number" placeholder="100" value={workTypeForm.price_per_unit} onChange={(e) => setWorkTypeForm({...workTypeForm, price_per_unit: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
              <Textarea placeholder="Optional description..." value={workTypeForm.description} onChange={(e) => setWorkTypeForm({...workTypeForm, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkTypeModal(false)}>Cancel</Button>
            <Button onClick={handleSaveWorkType} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingWorkType ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
